import { SocialPlatform } from '@app/modules/social-export/domain/platforms';
import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown';
import { Button, Menu, MenuItem } from '@mui/material';
import { useState } from 'react';
import { useIntl } from 'react-intl';
import { socialExportPublishMessages } from './social-export-publish-messages';

export interface SocialExportPublishPlatformDropdownProps {
  /** Platforms the user can open the upload wizard for. */
  platforms: SocialPlatform[];
  /** When true, the trigger stays disabled (e.g. no renderable media sources). */
  disabled: boolean;
  /** Called with the platform id when the user picks a menu entry. */
  onSelectPlatform: (platformId: string) => void;
}

/**
 * Presentational "Publish to" menu: one item per platform; parent handles navigation and data loading.
 */
export function SocialExportPublishPlatformDropdown({
  platforms,
  disabled,
  onSelectPlatform,
}: SocialExportPublishPlatformDropdownProps) {
  const { $t } = useIntl();
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const open = Boolean(anchorEl);

  const handleClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handlePlatformSelect = (platformId: string) => {
    onSelectPlatform(platformId);
    handleClose();
  };

  if (platforms.length === 0) {
    return null;
  }

  return (
    <>
      <Button
        size="small"
        disabled={disabled}
        onClick={handleClick}
        endIcon={<ArrowDropDownIcon />}
      >
        {$t(socialExportPublishMessages.publishTo)}
      </Button>
      <Menu
        anchorEl={anchorEl}
        open={open}
        onClose={handleClose}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
        transformOrigin={{ vertical: 'top', horizontal: 'left' }}
      >
        {platforms.map((platform) => (
          <MenuItem key={platform.id} onClick={() => handlePlatformSelect(platform.id)}>
            {platform.Icon({ sx: { mr: 1, fontSize: 20 }, colored: true })} {platform.name}
          </MenuItem>
        ))}
      </Menu>
    </>
  );
}
